
import {
  formatDateNormal,
  formatPriceWithoutCurrency,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import { Pagination } from "@/domain/entities/Pagination";
import { computed, defineComponent, nextTick, ref, watch } from "vue";
import TableBillDetail from "./TableBillDetail.vue";
import Select from "@/app/ui/components/select/index.vue";
import InvoiceRecipientTagPicker from "./InvoiceRecipientTagPicker.vue";
import {
  Invoice,
  InvoiceDetail,
  RecipientTag,
  SuccessInvoiceData
} from "@/domain/entities/PrintReceipt";
import { PrintReceiptController } from "@/app/ui/controllers/PrintReceiptController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { CreateInvoiceDetailBillApiRequest, ActivityRequest } from "@/data/payload/api/PrintReceiptApiRequest";
import { ResponsePayloadV2 } from "@/domain/entities/ResponsePayload";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import ModalSuccessReceipt from "./modal-success-receipt.vue";
import { useRoute, useRouter } from "vue-router";
import { UpdateInvoiceRequest, CreateInvoiceApiRequest } from "@/data/payload/api/PrintReceiptApiRequest";
import { onMounted } from "vue";
import InvoicePdfA4 from "./invoice/InvoicePdfA4.vue";
import InvoicePdfStruk from "./invoice/InvoicePdfStruk.vue";
import formatPrice from "@/app/infrastructures/misc/common-library/v2/FormatPrice";
import { useI18n } from 'vue-i18n';
import { AccountController } from '@/app/ui/controllers/AccountController';

export default defineComponent({
  name: "FormInvoiceDetail",

  props: {
    shipmentData: {
      type: Array as () => any[],
      default: () => []
    },
    header: {
      type: Object,
      required: false,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      default: () => {}
    }
  },

  components: {
    TableBillDetail,
    InvoiceRecipientTagPicker,
    ModalSuccessReceipt,
    InvoicePdfA4,
    InvoicePdfStruk
  },

  setup(props) {
    const { t } = useI18n();
    const note = ref("");
    const localShipments = ref<any[]>([]);
    const pagination = ref<Pagination>(new Pagination(1, 10));
    const isOpenSortNameType = ref<boolean>(false);
    const recipieValue = ref<any>("");
    const recipieNameValue = ref<string>("");
    const recipiePhoneValue = ref<string>("");
    const recipientTags = ref<RecipientTag[]>([]);
    const showList = ref<boolean>(false);
    const modalSuccessMode = ref<string>("create");
    const invoice = ref<any>(null);
    const struk = ref<any>(null);

    const route = useRoute();
    const router = useRouter();

    const startIndex = computed(
      () => (pagination.value.page - 1) * pagination.value.limit
    );

    const paginatedShipments = computed(() => {
      const start = startIndex.value;
      const end = start + pagination.value.limit;
      return localShipments.value.slice(start, end);
    });

    const totalSTT = computed(() => localShipments.value.length);

    const totalTariff = computed(() =>
      localShipments.value.reduce(
        (total, item) => total + (item.tariff || 0),
        0
      )
    );

    const totalAmount = computed(() =>
      localShipments.value.reduce(
        (total, item) => total + (item.amount || 0),
        0
      )
    );

    const accountCurrency = computed(
      () =>
        (AccountController.accountData?.accountCurrency as "IDR" | "MYR") ??
        "IDR"
    );

    const maskSensitiveData = (contact: string): string => {
      if (!contact) return "";

      // Mask phone: 087***2738
      if (contact.length <= 6) {
        return contact;
      }
      const start = contact.substring(0, 3);
      const end = contact.substring(contact.length - 4);
      return `${start}***${end}`;
    };

    const recipientNameData = computed(() => {
      const map = new Map();
      map.set("", {
        name: t("tagihan.form.selectBilledTo"),
        value: ""
      });

      for (const item of props.shipmentData) {
        if (!item.senderName) continue;

        const key = `${item.senderName} ${item.senderPhone || ""}`;

        map.set(
          key,
          map.get(key) ?? {
            name: item.senderPhone
              ? `${item.senderName} (${maskSensitiveData(item.senderPhone)})`
              : item.senderName,
            value: key
          }
        );
      }

      return [...map.values()];
    });

    const sendersName = computed(() => {
      const uniqueMap = new Map();
      let hasRealData = false;

      props.shipmentData.forEach((item: any) => {
        const name = item?.senderName?.trim();
        const phone = item?.senderPhone?.trim();

        if (name || phone) {
          hasRealData = true;

          if (!uniqueMap.has(name || phone)) {
            uniqueMap.set(name || phone, {
              name: name || "-",
              phone: phone || "-"
            });
          }
        }
      });

      if (!hasRealData) {
        return [{ name: "-", phone: "-" }];
      }

      return Array.from(uniqueMap.values());
    });

    const toggleList = () => {
      showList.value = !showList.value;
    };

    const onOpenSortNameType = () => {
      isOpenSortNameType.value = true;
    };

    const onCloseSelectSortNameType = () => {
      isOpenSortNameType.value = false;
    };

    const onSelectSortNameType = (key: string, value: string) => {
      const [name, phone] = value.split(";");
      recipieValue.value = value as any;
      recipieNameValue.value = name.trim();
      recipiePhoneValue.value = phone.trim();
    };

    const onPageChange = (page: number) => {
      pagination.value.page = page;

      const totalAll = localShipments.value.length;
      const used = (page - 1) * pagination.value.limit;

      pagination.value.totalData = Math.max(totalAll - used, 0);
    };

    const formatCurrency = (val: number) => {
      return formatPrice(val, "", accountCurrency.value);
    };

    const onDeleteShipment = (sttNo: string) => {
      localShipments.value = localShipments.value.filter(
        (item) => item.sttNo !== sttNo
      );

      const total = localShipments.value.length;

      pagination.value.allTotalData = total;
      pagination.value.totalData = total;

      const maxPage = Math.ceil(total / pagination.value.limit) || 1;

      if (pagination.value.page > maxPage) {
        pagination.value = new Pagination(
          maxPage,
          pagination.value.limit,
          total
        );
      }
    };

    const column = computed<any[]>(() => [
      {
        name: t("tagihan.table.no") + ".",
        field: "no",
        style: { width: "80px" }
      },
      {
        name: t("tagihan.table.noStt"),
        field: "sttNo",
        slotName: "sttNo",
        style: { width: "250px" }
      },
      {
        name: t("tagihan.table.createdAt"),
        field: "createdAt",
        style: { width: "250px" },
        render: (item: any) => ({
          text: formatDateNormal(item.createdAt, "DD MMMM YYYY, HH:mm")
        })
      },
      {
        name: t("tagihan.table.recipient"),
        field: "recipientName",
        slotName: "recipientName",
        style: { width: "188.5px" }
      },
      {
        name: t("tagihan.table.destinationCity"),
        field: "destinationName",
        style: { width: "188.5px" },
        render: (item: any) => ({
          text: item.destinationCity ? item.destinationCity : "-"
        })
      },
      {
        name: t("tagihan.table.productType"),
        field: "productType",
        slotName: "productType",
        style: { width: "280px" }
      },
      {
        name: t("tagihan.table.totalTariff"),
        field: "tariff",
        slotName: "tariff",
        style: { width: "280px" },
        render: (item: any) => ({
          text: formatCurrency(item.tariff),
          class: "font-semibold text-sm text-gray-900"
        })
      },
      {
        name: t("tagihan.table.totalAmount"),
        field: "amount",
        slotName: "amount",
        style: {
          width: "280px",
          boxShadow: "-4px 0 6px -4px rgba(0,0,0,0.08)"
        },
        isCollast: true
      },
      {
        name: t("tagihan.table.action"),
        slotName: "action",
        style: { width: "84px" },
        isCollast: true
      }
    ]);

    const mapStts = computed(() => {
      return localShipments.value.map((item: any) => item.sttNo);
    });

    const mapSendTo = computed(() => {
      return recipientTags.value.map((item: any) => ({
        //via: item.type,
        //value: item.type === "email" ? item.contact : null,
        //phone: item.type === "whatsapp" ? item.contact : null,
        //format: item.format
        via: item.type,
        viaExpanded: false,
        value: item.contact,
        format: item.format,
        formatExpanded: false,
        error: false,
        errorMessage: "",
        errorFormat: false,
        errorFormatMessage: ""
      }));
    });

    const onSendInvoice = async (invoiceDetail: Invoice) =>  {
      await nextTick();
      await nextTick();
      await nextTick();
      await nextTick();
      await nextTick();
      await nextTick();
      const pdf = await invoice.value?.generatePdfA4();
      const png = await invoice.value?.generatePngA4();
      const pdfStruk = await struk.value?.generatePdfStruk();

      // donwload
      if (mapSendTo.value[0].via === "whatsapp") {
        if (mapSendTo.value[0].format === "pdf_a4") {
          await invoice.value?.downloadPdfA4();
        } else if (mapSendTo.value[0].format === "png_a4") {
          await invoice.value?.downloadPngA4();
        } else if (mapSendTo.value[0].format === "pdf_struk") {
          await struk.value?.downloadPdfStruk();
        }
      }

      PrintReceiptController.processSendInvoice({
        validReceivers: mapSendTo.value,
        invoiceId: invoiceDetail.header.invoiceId,
        invoiceNo: invoiceDetail.header.invoiceNo,
        pdf,
        pdfStruk,
        png
      });
    }

    const onCreate = async () => {
      try {
        MainAppController.closeMessageModal()
        MainAppController.closeErrorMessage();
        MainAppController.showLoading();

        // create invoice
        const response: ResponsePayloadV2 = await PrintReceiptController.create(
          new CreateInvoiceApiRequest({
          sttNumbers: mapStts.value,
          billedTo: `${recipieNameValue.value} (${recipiePhoneValue.value})`,
          billedToName: recipieNameValue.value,
          billedToPhone: recipiePhoneValue.value,
          note: note.value,
        })
        );

        // get invoice detail
        const detail = await PrintReceiptController.getDetailInvoice(
          response.data.id
        );
        const invoiceDetail = new Invoice({
          ...detail,
          header: new InvoiceDetail({
            ...detail.header,
            invoiceId: response.data.id
          })
        });

        modalSuccessMode.value = "create";
        // set invoice
        PrintReceiptController.setInvoiceDetailData(invoiceDetail);
        await nextTick();

        // send email 
        if (mapSendTo.value?.length > 0) {
          await onSendInvoice(invoiceDetail);
        }

        PrintReceiptController.setSuccessCreateInvoiceData(
          new SuccessInvoiceData({
            invoiceId: invoiceDetail.header.invoiceId,
            invoiceNo: invoiceDetail.header.invoiceNo,
            bookedBy: invoiceDetail.header.billedToName,
            bookedByPhone: invoiceDetail.header.billedToPhone,
            totalStt: invoiceDetail.header.totalStt,
            totalAmount: invoiceDetail.header.totalAmount,
            balanceDue: invoiceDetail.header.balanceDue
          })
        );

        PrintReceiptController.setSuccessCreateInvoice(true);

      } catch (error) {
        MainAppController.showErrorMessage(
          parsingErrorResponse(error, "Buat tagihan gagal !")
        );
      } finally {
        MainAppController.closeLoading();
      }
    };

    const goBack = () => {
      router.push("/shipment/receipt-v2");
    };
    const buttonActionTitle = computed(() => {
      const id = route.params.id;
      return id
        ? t("tagihan.form.saveInvoice")
        : t("tagihan.form.createInvoice");
    });

    const updateReceipt = async (id: number) => {
      try {
        MainAppController.showLoading();
        const payload = new UpdateInvoiceRequest(
          mapStts.value,
          `${recipieNameValue.value} (${recipiePhoneValue.value})`,
          recipieNameValue.value,
          recipiePhoneValue.value,
          note.value
        );

        // update invoice
        const res = await PrintReceiptController.updateInvoice({ id, payload });

        // get detail invoice
        const detail = await PrintReceiptController.getDetailInvoice(
          res.invoiceId
        );

        const invoiceDetail = new Invoice({
          ...detail,
          header: new InvoiceDetail({
            ...detail.header,
            invoiceId: res.invoiceId
          })
        });

        modalSuccessMode.value = "update";

        // set invoice
        PrintReceiptController.setInvoiceDetailData(invoiceDetail);

        // send email 
        if (mapSendTo.value?.length > 0) {
          await onSendInvoice(invoiceDetail);
        }

        PrintReceiptController.setSuccessCreateInvoiceData(
          new SuccessInvoiceData({
            invoiceId: detail.header.invoiceId,
            invoiceNo: detail.header.invoiceNo,
            bookedBy: detail.header.billedToName,
            bookedByPhone: detail.header.billedToPhone,
            totalStt: detail.header.totalStt,
            totalAmount: detail.header.totalAmount,
            balanceDue: detail.header.balanceDue
          })
        );
        PrintReceiptController.setSuccessCreateInvoice(true);
      } catch (err) {
        MainAppController.showErrorMessage(
          parsingErrorResponse(err, "Gagal Mengupdate Tagihan!", () => {
            updateReceipt(id);
            MainAppController.closeErrorMessage();
          })
        );
      } finally {
        MainAppController.closeLoading();
      }
    };

    const onHandleSubmit = () => {
      const id = route.params.id;
      if (id) {
        updateReceipt(Number(id));
      } else {
        MainAppController.showMessageModal(
          new ModalMessageEntities({
            title: t("tagihan.form.confirmCreateTitle"),
            message: t("tagihan.form.confirmCreateMessage"),
            image: "badge-confirmation-general",
            textCancel: t("tagihan.form.cancel"),
            textSuccess: t("tagihan.form.createInvoice"),
            onSubmit: () => onCreate(),
            onClose: () => MainAppController.closeMessageModal()
          })
        );
      }
    };

    const isSuccessCreateInvoice = computed(() => {
      return PrintReceiptController.isSuccessCreateInvoice;
    });

    const successCreateInvoiceData = computed(() => {
      return PrintReceiptController.successCreateInvoiceData;
    });

    const onCloseSuccessCreateInvoice = () => {
      PrintReceiptController.resetCreateInvoiceFromSuccessBooking();
      PrintReceiptController.setSuccessCreateInvoiceData();
      PrintReceiptController.setInvoiceDetailData();
      const id = route.params.id;
      if (id) {
        goBack();
      }
    };

    const handleDownloadCreateInvoice = async (option: { key: string }) => {
      try {
        MainAppController.showLoading();

        const detail = await PrintReceiptController.getDetailInvoice(
          PrintReceiptController.invoiceDetailData.header.invoiceId
        );
        PrintReceiptController.setInvoiceDetailData(
          new Invoice({
            ...detail,
            header: {
              ...detail.header,
              invoiceId:
                PrintReceiptController.invoiceDetailData.header.invoiceId
            }
          })
        );
        await nextTick();

        if (option.key === "pdf_a4") {
          await invoice.value?.downloadPdfA4();
        } else if (option.key === "png_a4") {
          await invoice.value?.downloadPngA4();
        } else if (option.key === "struk") {
          await struk.value?.downloadPdfStruk();
        }
      } catch (error) {
        MainAppController.showErrorMessage(
          parsingErrorResponse(error, t("tagihan.errorGetDetail"), () => {
            MainAppController.closeErrorMessage();
            handleDownloadCreateInvoice(option);
          })
        );
      } finally {
        MainAppController.closeLoading();
      }
      PrintReceiptController.activity({
        id: PrintReceiptController.invoiceDetailData.header.invoiceId,
        payload: new ActivityRequest(true)
      });
    };

    const mostFrequentRecipient = computed(() => {
      const shipment = props.shipmentData;
      if (!shipment.length) return null;

      const count: Record<string, number> = {};

      // Count frequency
      shipment.forEach((item) => {
        count[item.senderName] = (count[item.senderName] || 0) + 1;
      });

      const max = Math.max(...Object.values(count));

      // Return first appearing name with max frequency
      for (const item of shipment) {
        if (count[item.senderName] === max) {
          return item;
        }
      }

      return null;
    });

    watch(
      () => props.shipmentData,
      (val: any[]) => {
        localShipments.value = [...val];
        pagination.value.page = 1;
        pagination.value.allTotalData = val.length;
        pagination.value.totalData = val.length;
        if (!route.params.id) {
          const shipment = mostFrequentRecipient.value;
          if (shipment) {
            const key = `${shipment.senderName} ${shipment.senderPhone || ""}`;
            recipieValue.value = key;
            recipieNameValue.value = shipment.senderName;
            recipiePhoneValue.value = shipment.senderPhone;
          }
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      const id = route.params.id;
      if (id && props.header) {
        note.value = props.header.note;
        const key = `${props.header.billedToName} ${props.header.billedToPhone || ""}`;
        recipieValue.value = key; 
        recipieNameValue.value = props.header.billedToName;
        recipiePhoneValue.value = props.header.billedToPhone;
        recipientTags.value = props.header.billedToSend?.map((item: any) => {
          return {
            type: item.type,
            contact: item.contact,
            format: item.format
          }
        });
      }
    });

    const invoiceDetail = computed(() => {
      return PrintReceiptController.invoiceDetailData;
    });

    return {
      column,
      note,
      recipientTags,
      pagination,
      paginatedShipments,
      startIndex,
      totalSTT,
      totalTariff,
      totalAmount,
      recipientNameData,
      isOpenSortNameType,
      recipieNameValue,
      successCreateInvoiceData,
      isSuccessCreateInvoice,
      sendersName,
      showList,
      invoice,
      struk,
      formatCurrency,
      onPageChange,
      onDeleteShipment,
      onOpenSortNameType,
      onCloseSelectSortNameType,
      onSelectSortNameType,
      formatPriceWithoutCurrency,
      onHandleSubmit,
      onCloseSuccessCreateInvoice,
      handleDownloadCreateInvoice,
      toggleList,
      buttonActionTitle,
      modalSuccessMode,
      updateReceipt,
      invoiceDetail,
      maskSensitiveData,
      recipieValue,
      formatPrice,
      accountCurrency
    };
  }
});
